import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { X, Copy, Receipt, Printer } from "@phosphor-icons/react";
import api, { formatApiError } from "../api";
import { BrutalButton, BrutalInput } from "./Brutal";
import { useAuth } from "../AuthContext";
import { openPackingSlip } from "../lib/packingSlip";

// v401.1 Payment Identification — build a Sale Request BEFORE sending the
// e-transfer. Returns a unique memo code (e.g. RV01-7294) the sender pastes
// into the e-transfer memo so the Master Admin can identify the payment.
// isMaster=true -> owner sells straight from Master Inventory (god mode).
// v414.71: onBehalfOf={id,name} -> org role creates the request AS that
// distributor; fulfill-from-master toggle ships straight from Master stock.
export default function SaleRequestDialog({ isMaster = false, initialProductId = null, onBehalfOf = null, onClose, onCreated }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [rows, setRows] = useState([]); // available stock
  const [qty, setQty] = useState({}); // product_id -> quantity
  const [prices, setPrices] = useState({}); // product_id -> unit price override
  const [customer, setCustomer] = useState("");
  const [note, setNote] = useState("");
  const [fromMaster, setFromMaster] = useState(false); // v414.71
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null); // success view with the code

  const holderId = onBehalfOf?.id || user.id;

  useEffect(() => {
    let src;
    if (isMaster) {
      src = api.get("/products");
    } else if (fromMaster) {
      // master catalog: owner on-behalf reads /products; a distributor reads
      // the supplier portal catalog (their own pricing view).
      src = onBehalfOf
        ? api.get("/products")
        : api.get("/restock-requests/catalog").then(({ data }) => ({
            data: (data || []).filter((r) => r.available).map((r) => ({
              id: r.id, name: r.name, unit: r.unit,
              current_stock: null, wholesale_price: r.ws,
              suggested_retail_price: r.srp,
            })),
          }));
    } else {
      src = api.get(`/distributors/${holderId}/inventory`);
    }
    src
      .then(({ data }) => {
        const list = (data || [])
          .filter((r) => r.current_stock === null || (r.current_stock || 0) > 0)
          .filter((r) => !r.coming_soon)
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setRows(list);
        setQty({});
        setPrices({});
        // v401.7: opened from a card's $ Direct Sale button — preselect it.
        if (initialProductId && !fromMaster) {
          const hit = list.find((r) => r.id === initialProductId || r.source_product_id === initialProductId);
          if (hit) setQty((q) => ({ ...q, [hit.id]: 1 }));
        }
      })
      .catch((e) => toast.error(formatApiError(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromMaster]);

  const defaultPrice = (r) =>
    isMaster
      ? (r.suggested_retail_price ?? r.wholesale_price ?? 0)
      : (r.wholesale_price ?? 0);

  const effPrice = (r) =>
    prices[r.id] !== undefined && prices[r.id] !== "" ? Number(prices[r.id]) : Number(defaultPrice(r));

  const selected = useMemo(
    () => rows.filter((r) => Number(qty[r.id]) > 0),
    [rows, qty]
  );
  const total = useMemo(
    () => selected.reduce((sum, r) => sum + effPrice(r) * Number(qty[r.id]), 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selected, qty, prices]
  );
  // v401.5 Distributor POS: payout = Σ (price charged − wholesale) × qty
  const payout = useMemo(
    () =>
      isMaster
        ? null
        : selected.reduce(
            (sum, r) => sum + (effPrice(r) - Number(r.wholesale_price || 0)) * Number(qty[r.id]),
            0
          ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selected, qty, prices, isMaster]
  );

  const submit = async () => {
    if (selected.length === 0) {
      toast.error(t("sale_request.pick_items"));
      return;
    }
    for (const r of selected) {
      const p = prices[r.id];
      if (p !== undefined && p !== "" && (!Number.isFinite(Number(p)) || Number(p) < 0)) {
        toast.error(t("sale_request.invalid_price"));
        return;
      }
    }
    setBusy(true);
    try {
      const { data } = await api.post("/sale-requests", {
        items: selected.map((r) => ({
          product_id: r.id,
          quantity: Number(qty[r.id]),
          unit_price: prices[r.id] !== undefined && prices[r.id] !== "" ? Number(prices[r.id]) : undefined,
        })),
        customer_name: customer.trim() || undefined,
        note: note.trim() || undefined,
        on_behalf_of_id: onBehalfOf?.id || undefined,
        fulfill_from_master: fromMaster || undefined,
      });
      setCreated(data);
      onCreated && onCreated(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(created.code).then(
      () => toast.success(t("sale_request.code_copied")),
      () => toast.error(t("sale_request.copy_failed"))
    );
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="sale-request-dialog">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-xl font-semibold flex items-center gap-2">
              <Receipt size={20} weight="bold" className="text-[#8a6a1c]" />
              {onBehalfOf
                ? t("sale_request.title_on_behalf", { name: onBehalfOf.name })
                : isMaster ? t("sale_request.title_master") : t("sale_request.title")}
            </h3>
            {!created && <p className="text-xs text-zinc-500 mt-1">{t("sale_request.subtitle")}</p>}
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-700" data-testid="sale-request-close">
            <X size={20} weight="bold" />
          </button>
        </div>

        {created ? (
          <div className="space-y-4" data-testid="sale-request-success">
            <div className="rounded-xl border-2 border-[#c9a24a] bg-[#fbf6e7] p-5 text-center">
              <p className="text-xs font-semibold uppercase tracking-widest text-[#8a6a1c]">{t("sale_request.your_code")}</p>
              <p className="text-4xl font-mono font-bold tracking-wider mt-2" data-testid="sale-request-code">{created.code}</p>
              <BrutalButton size="sm" variant="secondary" onClick={copyCode} className="mt-3" data-testid="sale-request-copy">
                <Copy size={14} weight="bold" /> {t("sale_request.copy_code")}
              </BrutalButton>
            </div>
            <div className="rounded-lg bg-zinc-950 text-white p-4 text-sm font-medium" data-testid="sale-request-instruction">
              {t("sale_request.memo_instruction")}
            </div>
            <div className="text-sm text-zinc-600">
              {created.items.map((it) => (
                <div key={it.product_id} className="flex justify-between py-0.5">
                  <span>{it.quantity}x {it.product_name}</span>
                  <span className="tabular-nums">${it.line_total.toFixed(2)}</span>
                </div>
              ))}
              <div className="flex justify-between border-t border-zinc-200 mt-1 pt-1 font-semibold text-zinc-900">
                <span>{t("sale_request.total")}</span>
                <span data-testid="sale-request-total">${created.total_amount.toFixed(2)}</span>
              </div>
              {created.expected_payout != null && (
                <div className="flex justify-between pt-1 font-semibold text-emerald-700">
                  <span>{t("sale_request.payout_once_paid")}</span>
                  <span data-testid="sale-request-payout">${created.expected_payout.toFixed(2)}</span>
                </div>
              )}
            </div>
            <BrutalButton className="w-full" onClick={onClose} data-testid="sale-request-done">
              {t("common.done")}
            </BrutalButton>
            <BrutalButton
              variant="secondary"
              className="w-full"
              onClick={() => { if (!openPackingSlip(created)) toast.error(t("sale_request.popup_blocked")); }}
              data-testid="sale-request-packing-slip"
            >
              <Printer size={15} weight="bold" /> {t("sale_request.packing_slip")}
            </BrutalButton>
          </div>
        ) : (
          <div className="space-y-4">
            {!isMaster && (
              <label className="flex items-start gap-2 cursor-pointer select-none rounded-lg border border-zinc-200 p-2.5 bg-zinc-50" data-testid="sale-request-from-master-row">
                <input
                  type="checkbox"
                  checked={fromMaster}
                  onChange={(e) => setFromMaster(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-[#c9a24a]"
                  data-testid="sale-request-from-master"
                />
                <span className="text-xs">
                  <span className="font-semibold">{t("sale_request.fulfill_master_label")}</span>
                  <span className="block text-zinc-500 mt-0.5">{t("sale_request.fulfill_master_hint")}</span>
                </span>
              </label>
            )}
            <div className="space-y-2 max-h-64 overflow-y-auto pr-1" data-testid="sale-request-items">
              {rows.length === 0 && <p className="text-sm text-zinc-500">{t("sale_request.no_stock")}</p>}
              {rows.map((r) => (
                <div key={r.id} className={`flex items-center gap-2 rounded-lg border p-2 ${Number(qty[r.id]) > 0 ? "border-[#c9a24a] bg-[#fbf6e7]/50" : "border-zinc-200"}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{r.name}</p>
                    <p className="text-[11px] text-zinc-500">
                      {r.current_stock != null ? `${r.current_stock} ${r.unit || "units"}` : t("sale_request.from_master_tag")} · {isMaster ? "" : `${t("pricing.wholesale_short")} `}${Number(isMaster ? defaultPrice(r) : (r.wholesale_price || 0)).toFixed(2)}
                      {!isMaster && Number(qty[r.id]) > 0 && (effPrice(r) - Number(r.wholesale_price || 0)) !== 0 && (
                        <span
                          className={`ml-1.5 font-semibold ${(effPrice(r) - Number(r.wholesale_price || 0)) > 0 ? "text-emerald-700" : "text-rose-700"}`}
                          data-testid={`sale-request-row-payout-${r.id}`}
                        >
                          {(effPrice(r) - Number(r.wholesale_price || 0)) > 0 ? "+" : ""}
                          ${(((effPrice(r) - Number(r.wholesale_price || 0)) * Number(qty[r.id]))).toFixed(2)}
                        </span>
                      )}
                    </p>
                  </div>
                  <input
                    type="number"
                    min="0"
                    max={r.current_stock ?? undefined}
                    value={qty[r.id] ?? ""}
                    placeholder="0"
                    onChange={(e) => setQty({ ...qty, [r.id]: e.target.value })}
                    className="w-16 border border-zinc-300 rounded-lg px-2 py-1.5 text-sm text-center"
                    data-testid={`sale-request-qty-${r.id}`}
                  />
                  {Number(qty[r.id]) > 0 && (
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={prices[r.id] ?? ""}
                      placeholder={`$${Number(defaultPrice(r)).toFixed(2)}`}
                      onChange={(e) => setPrices({ ...prices, [r.id]: e.target.value })}
                      className="w-20 border border-zinc-300 rounded-lg px-2 py-1.5 text-sm text-right"
                      title={isMaster ? t("sale_request.unit_price") : t("sale_request.price_charged")}
                      data-testid={`sale-request-price-${r.id}`}
                    />
                  )}
                </div>
              ))}
            </div>
            {(isMaster || onBehalfOf) && (
              <BrutalInput
                value={customer}
                onChange={(e) => setCustomer(e.target.value)}
                placeholder={t("sale_request.customer_placeholder")}
                data-testid="sale-request-customer"
              />
            )}
            <BrutalInput
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("sale_request.note_placeholder")}
              data-testid="sale-request-note"
            />
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-zinc-600">{t("sale_request.total")}</span>
              <span className="text-xl font-semibold tabular-nums" data-testid="sale-request-live-total">${total.toFixed(2)}</span>
            </div>
            {!isMaster && selected.length > 0 && (
              <div className="flex items-center justify-between rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2">
                <span className="text-sm font-medium text-emerald-800">{t("sale_request.your_payout")}</span>
                <span
                  className={`text-lg font-semibold tabular-nums ${payout >= 0 ? "text-emerald-700" : "text-rose-700"}`}
                  data-testid="sale-request-payout-live"
                >
                  ${payout.toFixed(2)}
                </span>
              </div>
            )}
            <BrutalButton className="w-full" onClick={submit} disabled={busy || selected.length === 0} data-testid="sale-request-submit">
              {busy ? t("assign.sending") : t("sale_request.generate_code")}
            </BrutalButton>
          </div>
        )}
      </div>
    </div>
  );
}
